import { useEffect, useRef, useState } from "react";
import "./boot-screen.css";

/**
 * HealthOS Boot - splash overlay.
 *
 * Mirrors the design handoff (`HealthOS Boot.html`) pixel-for-pixel: hairline
 * ring, raised disc, layered leaf reveal, ripples, mono wordmark, mono corner
 * labels, bottom progress hairline. The visual sequence is CSS-animation
 * driven; this component owns:
 *
 *   1. The progress driver - either an internal step-based RAF loop (one of
 *      the three preset profiles in {@link BOOT_PRESETS}) or a host-supplied
 *      `progress` prop that overrides it. The first time the host writes a
 *      value, the internal driver cancels.
 *
 *   2. The status label - auto-advances through {@link BOOT_PHASES} as
 *      progress crosses each threshold. Host can override with `status`.
 *
 *   3. The wipe - when progress reaches 1.0 (host- or auto-driven), or the
 *      host calls `finish()`, the displayed bar owns a final ramp to 100%,
 *      holds there briefly, then starts the wipe and calls `onFinish`.
 *
 * Use {@link useBoot} from BootContext for the host-driven flow; mount this
 * component once at the app root and let the context drive `progress` /
 * `status` / `onFinish` from anywhere in the tree.
 */

export type BootPreset = "quick" | "general" | "cold";

interface BootStep {
  /** Progress value to ramp to (0..1). */
  target: number;
  /** Milliseconds spent ramping to `target`. */
  rampMs: number;
  /** Milliseconds the bar holds before the next step. */
  holdMs: number;
}

/**
 * Three step-rhythm profiles. Each preset's last step always lands at 1.0
 * so the auto-driver finishes on its own; the host can interrupt at any
 * point with a `progress` prop write.
 */
const BOOT_PRESETS: Record<BootPreset, BootStep[]> = {
  quick: [
    { target: 0.3, rampMs: 180, holdMs: 220 },
    { target: 0.7, rampMs: 280, holdMs: 380 },
    { target: 1.0, rampMs: 320, holdMs: 240 },
  ],
  general: [
    { target: 0.2, rampMs: 240, holdMs: 260 },
    { target: 0.45, rampMs: 380, holdMs: 300 },
    { target: 0.7, rampMs: 360, holdMs: 340 },
    { target: 1.0, rampMs: 420, holdMs: 160 },
  ],
  cold: [
    { target: 0.12, rampMs: 320, holdMs: 200 },
    { target: 0.28, rampMs: 420, holdMs: 260 },
    { target: 0.46, rampMs: 380, holdMs: 220 },
    { target: 0.62, rampMs: 360, holdMs: 260 },
    { target: 0.8, rampMs: 420, holdMs: 160 },
    { target: 1.0, rampMs: 520, holdMs: 80 },
  ],
};

const FINISH_RAMP_MS = 520;
const FINISH_HOLD_MS = 120;
const CINEMATIC_HANDOFF_THRESHOLD = 0.85;

interface BootPhase {
  at: number;
  label: string;
}

/**
 * Phase labels keyed to progress thresholds. Host can override via the
 * `status` prop - e.g. "Generating your plan" while a real fetch runs.
 */
const BOOT_PHASES: BootPhase[] = [
  { at: 0.0, label: "Listening" },
  { at: 0.18, label: "Reading your signals" },
  { at: 0.45, label: "Composing your day" },
  { at: 0.85, label: "Ready" },
];

export interface BootScreenProps {
  /**
   * Host-driven progress value (0..1). When set, the internal step driver
   * cancels and the bar follows whatever the host writes. When undefined,
   * the internal driver runs the chosen `mode` to completion.
   */
  progress?: number;
  /**
   * Override the status label. When undefined, the label follows
   * {@link BOOT_PHASES} based on current progress.
   */
  status?: string;
  /**
   * Auto-driver step rhythm. Ignored when `progress` is host-driven.
   * @default "general"
   */
  mode?: BootPreset;
  /**
   * Mono label rendered in the top-left corner. Defaults to a build tag.
   */
  versionLabel?: string;
  /**
   * Mono label rendered in the top-right corner next to the heartbeat dot.
   */
  signalLabel?: string;
  /**
   * Called once the wipe completes. Host should unmount the component on
   * this callback so the boot doesn't sit invisibly on top of the app.
   */
  onFinish?: () => void;
  /**
   * When true, boot triggers its finish wipe regardless of progress. Useful
   * for "everything I was waiting on resolved, get me to /today now."
   */
  finishNow?: boolean;
  /** Fires whenever the internal wipe-state changes (idle → ramping →
   *  holding → igniting → wiping → fading → finished). Lets consumers
   *  sync audio / haptics with the boot's transition timeline. */
  onPhaseChange?: (phase: WipePhase) => void;
}

/**
 * How long the ignition holds before the wipe, which is when the snap fires.
 *
 * The ripple cascade's last ring runs to 3380ms, so the original 3100 cut the
 * finale off mid-flight and left the arrival chime no room at all: it landed
 * effectively on top of the snap rather than before it. This lets the rings
 * complete and settle, then holds while the chime rings out, and only then
 * snaps. Exported so the audio that is timed against it cannot drift.
 */
export const IGNITE_TO_WIPE_MS = 4500;

export type WipePhase =
  | "idle"
  | "ramping"
  | "holding"
  | "igniting"
  | "wiping"
  | "fading"
  | "finished";

export function BootScreen({
  progress,
  status,
  mode = "general",
  versionLabel = "HealthOS · v1.0.4",
  signalLabel = "Sensing",
  onFinish,
  finishNow,
  onPhaseChange,
}: BootScreenProps) {
  /**
   * Single source of truth for the bar width. Either reflects host-supplied
   * `progress` or the auto-driver's RAF-emitted value. Stored as a number so
   * the phase computation can read it without re-deriving from the DOM.
   */
  const [autoProgress, setAutoProgress] = useState(0);
  const [finishProgress, setFinishProgress] = useState<number | null>(null);
  const effectiveProgress = finishProgress ?? progress ?? autoProgress;
  const clampedProgress = Math.max(0, Math.min(1, effectiveProgress));
  const visibleProgressRef = useRef<number>(clampedProgress);
  useEffect(() => {
    visibleProgressRef.current = clampedProgress;
  });

  /**
   * Whether the host has ever written a value to `progress`. Once true, the
   * internal driver shuts down for the lifetime of this BootScreen mount.
   */
  const hostDrivingRef = useRef<boolean>(progress !== undefined);

  /** RAF + cancellation flag for the internal step driver. */
  const rafRef = useRef<number | null>(null);
  const cancelledRef = useRef<boolean>(false);

  const [wipeState, setWipeState] = useState<WipePhase>("idle");
  useEffect(() => {
    onPhaseChange?.(wipeState);
  }, [wipeState, onPhaseChange]);

  // Auto-driver. Runs the chosen preset in sequence (ramp → hold → ramp) and
  // exits when it reaches 1.0. Cancels the moment the host passes a value
  // for `progress`.
  useEffect(() => {
    if (hostDrivingRef.current) return;
    cancelledRef.current = false;
    const steps = BOOT_PRESETS[mode];
    let stepIdx = 0;
    let currentValue = 0;

    const ramp = (target: number, ms: number, done: () => void) => {
      if (cancelledRef.current) return;
      const startValue = currentValue;
      const delta = target - startValue;
      const startTs = performance.now();
      const tick = (ts: number) => {
        if (cancelledRef.current) return;
        const t = Math.min(1, (ts - startTs) / ms);
        // ease-out cubic - feels "completing a task" rather than linear.
        const eased = 1 - (1 - t) ** 3;
        currentValue = startValue + delta * eased;
        setAutoProgress(currentValue);
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          done();
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const next = () => {
      if (cancelledRef.current) return;
      if (stepIdx >= steps.length) return; // wipe is triggered by the progress=1 effect below
      const step = steps[stepIdx];
      stepIdx = stepIdx + 1;
      if (!step) return;
      ramp(step.target, step.rampMs, () => {
        if (cancelledRef.current) return;
        window.setTimeout(next, step.holdMs);
      });
    };
    next();

    return () => {
      cancelledRef.current = true;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [mode]);

  // Cancel the auto-driver as soon as the host starts writing progress.
  useEffect(() => {
    if (progress === undefined) return;
    hostDrivingRef.current = true;
    cancelledRef.current = true;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
  }, [progress]);

  // Wipe state machine - three small effects, one per transition.
  //
  // Splitting it like this lets StrictMode's dev double-effect cancel-and-
  // restart any phase without us losing the overall sequence. The previous
  // single-effect version held all phases in one closure, so cleanup of
  // the first dev mount killed the wipe before the second mount could
  // pick it up; gating with a ref made the second mount bail entirely
  // and the boot got stuck at 100%.

  /*
   * Cinematic finale (holding → igniting → wiping → fading with the
   * leaf bounce + ripple cascade + iris) is reserved for `mode: "cold"`
   * which the post-onboarding orchestrator passes via `boot.show()`.
   * Page-refresh / general boots skip straight from ramping → fading
   * so the splash dismisses in ~400ms rather than ~5s.
   */
  const cinematic = mode === "cold";
  const afterRamp = cinematic ? "igniting" : "fading";

  useEffect(() => {
    if (wipeState !== "idle") return;
    if (finishNow) {
      setWipeState("ramping");
      return;
    }
    // Cinematic threshold overlaps ignition with the last preset step's
    // final ramp, but only when the auto-driver owns progress. Host-driven
    // flows (e.g. the post-onboarding ticker) must dismiss via finish().
    if (hostDrivingRef.current) return;
    const threshold = cinematic ? CINEMATIC_HANDOFF_THRESHOLD : 1;
    if (clampedProgress < threshold) return;
    setWipeState("ramping");
  }, [wipeState, finishNow, clampedProgress, cinematic]);

  useEffect(() => {
    if (wipeState !== "ramping") return;
    const startValue = visibleProgressRef.current;
    const startTs = performance.now();
    let raf: number | null = null;
    let holdTimer: number | null = null;
    setFinishProgress(startValue);
    const startHold = () => {
      setFinishProgress(1);
      holdTimer = window.setTimeout(() => setWipeState(afterRamp), FINISH_HOLD_MS);
    };
    if (startValue >= 1) {
      startHold();
      return () => {
        if (holdTimer !== null) window.clearTimeout(holdTimer);
      };
    }
    const tick = (ts: number) => {
      const t = Math.min(1, (ts - startTs) / FINISH_RAMP_MS);
      const eased = 1 - (1 - t) ** 3;
      setFinishProgress(startValue + (1 - startValue) * eased);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        startHold();
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      if (holdTimer !== null) window.clearTimeout(holdTimer);
    };
  }, [wipeState, afterRamp]);

  useEffect(() => {
    if (wipeState !== "holding") return;
    const timer = window.setTimeout(() => setWipeState("igniting"), 700);
    return () => window.clearTimeout(timer);
  }, [wipeState]);

  useEffect(() => {
    if (wipeState !== "igniting") return;
    const timer = window.setTimeout(() => setWipeState("wiping"), IGNITE_TO_WIPE_MS);
    return () => window.clearTimeout(timer);
  }, [wipeState]);

  useEffect(() => {
    if (wipeState !== "wiping") return;
    const timer = window.setTimeout(() => setWipeState("fading"), 600);
    return () => window.clearTimeout(timer);
  }, [wipeState]);

  useEffect(() => {
    if (wipeState !== "fading") return;
    const timer = window.setTimeout(() => {
      setWipeState("finished");
      onFinish?.();
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wipeState, onFinish]);

  if (wipeState === "finished") return null;

  // Status label: derive from phase thresholds unless the host overrode it.
  const derivedStatus = ((): string => {
    if (status) return status;
    let label = BOOT_PHASES[0]?.label ?? "Initializing";
    for (const phase of BOOT_PHASES) {
      if (clampedProgress >= phase.at) label = phase.label;
    }
    return label;
  })();

  return (
    <div
      role="progressbar"
      aria-label="HealthOS loading"
      aria-valuenow={Math.round(clampedProgress * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={derivedStatus}
      className={`healthos-boot${
        wipeState === "igniting" ? " is-igniting" : ""
      }${wipeState === "wiping" || wipeState === "fading" ? " is-wiping" : ""}${
        wipeState === "fading" ? " is-fading" : ""
      }`}
    >
      <div className="corner tl">{versionLabel}</div>
      <div className="corner tr">
        <span className="dot" />
        <span>{signalLabel}</span>
      </div>
      <div className="corner bl">
        <span>Biometric kernel · 04.12</span>
      </div>
      <div className="corner br">
        <BootStatus label={derivedStatus} />
      </div>

      <div
        className="progress"
        style={{ "--p": `${clampedProgress * 100}%` } as React.CSSProperties}
      />

      <div className="stage">
        {/* Class is `boot-ring` (not `ring`) so Tailwind's ring utility
            doesn't claim it and apply a 1px navy box-shadow around the SVG
            bounding box - that produced a clearly visible square frame
            wrapping the entire stage on first paint. */}
        <svg className="boot-ring" viewBox="0 0 320 320" aria-hidden="true">
          <circle cx="160" cy="160" r="140" />
        </svg>

        <div className="disc" aria-hidden="true" />

        <div className="ripples" aria-hidden="true">
          <div className="ripple r1" />
          <div className="ripple r2" />
          <div className="ripple r3" />
          <div className="ripple r4" />
          <div className="ripple r5 ig-only" />
          <div className="ripple r6 ig-only" />
          <div className="ripple r7 ig-only" />
          <div className="ripple r8 ig-only" />
        </div>

        <div className="leaf-wrap" aria-hidden="true">
          <svg viewBox="0 0 280 320" fill="none" role="img" aria-label="Thrive leaf">
            <title>Thrive leaf</title>
            <path
              className="s1"
              d="M138.72 0C155.93 0 172.41 3.07 187.66 8.68V8.69C127.19 56.83 67.91 115.27 8.79 185.02C3.24 169.86 0 154.74 0 140.44C0 62.88 60.79 0 138.72 0Z"
              fill="#b7dde1"
            />
            <path
              className="s2"
              d="M249.81 53.84C268.61 77.7 279.83 107.76 279.83 140.44C279.83 209.36 201.81 297.56 154.76 316.37C154.76 316.37 154.76 316.36 154.76 316.36C169.54 205.88 213.82 119.16 249.81 53.84Z"
              fill="#87cfd4"
            />
            <path
              className="s3"
              d="M8.79 185.02C26.91 234.54 69.71 284.45 105.16 307.23C143.24 205.62 190.76 104.78 238.84 41.49C224.51 27.13 207.1 15.84 187.66 8.69C127.19 56.83 67.91 115.27 8.79 185.02Z"
              fill="#459fa5"
            />
            <path
              className="s4"
              d="M138.72 320C143.43 320 148.85 318.72 154.76 316.36C169.54 205.88 213.82 119.16 249.81 53.83C246.4 49.5 242.74 45.38 238.85 41.48C190.77 104.77 143.25 205.61 105.17 307.22C117.77 315.32 129.44 319.99 138.73 319.99L138.72 320Z"
              fill="#14818f"
            />
          </svg>
        </div>

        <div className="word">
          <h1 aria-label="HealthOS">
            <BootTypedSpan className="word-health" full="Health" startMs={1320} />
            <BootTypedSpan className="word-os" full="OS" startMs={1320 + 6 * 55 + 60} />
            <span className="word-caret" aria-hidden="true">
              _
            </span>
          </h1>
        </div>
      </div>
    </div>
  );
}

function BootStatus({ label }: { label: string }) {
  const [shown, setShown] = useState(label);
  const [opacity, setOpacity] = useState<number>(1);
  useEffect(() => {
    if (label === shown) return;
    setOpacity(0.25);
    const t = window.setTimeout(() => {
      setShown(label);
      setOpacity(1);
    }, 120);
    return () => window.clearTimeout(t);
  }, [label, shown]);
  return (
    <span className="status" style={{ opacity }}>
      {shown || " "}
    </span>
  );
}

/**
 * Mono characters that "type in" one at a time after a delay. Drives the
 * `Health` and `OS` halves of the wordmark; respects reduced motion by
 * landing on the full string immediately.
 */
function BootTypedSpan({
  className,
  full,
  startMs,
}: {
  className: string;
  full: string;
  startMs: number;
}) {
  const TYPE_MS = 55;
  const [shown, setShown] = useState("");
  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(full);
      return;
    }
    const timers: number[] = [];
    for (let i = 1; i <= full.length; i++) {
      const count = i;
      timers.push(
        window.setTimeout(() => setShown(full.slice(0, count)), startMs + (count - 1) * TYPE_MS),
      );
    }
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [full, startMs]);
  return <span className={className}>{shown}</span>;
}
